import { useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import { getDataClient } from "@/lib/dataClient";
import { examTypes } from "@/data/panels";

const inputClass =
  "w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none transition-colors";
const labelClass = "block text-sm font-medium text-foreground-700 mb-1.5";

const difficulties = [
  { value: "", label: "Karışık (tüm zorluklar)" },
  { value: "easy", label: "Kolay" },
  { value: "medium", label: "Orta" },
  { value: "hard", label: "Zor" },
  { value: "very_hard", label: "Çok Zor" },
];

interface Result {
  added: number;
  requested: number;
}

export default function DenemeOlustur() {
  const [title, setTitle] = useState("");
  const [examType, setExamType] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [duration, setDuration] = useState("");
  const [questionCount, setQuestionCount] = useState("");
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage(null);
    setResult(null);

    if (!examType) {
      setMessage({ type: "error", text: "Lütfen bir sınav türü seçin." });
      return;
    }
    if (!questionCount || Number(questionCount) < 1) {
      setMessage({ type: "error", text: "Geçerli bir soru sayısı girin." });
      return;
    }

    const finalTitle = title.trim() || `${examType} AI Deneme ${new Date().getFullYear()}`;

    setGenerating(true);
    try {
      const dataClient = getDataClient();
      const { data, error } = await dataClient.rpc("ai_generate_exam", {
        p_title: finalTitle,
        p_exam_type: examType,
        p_difficulty: difficulty || null,
        p_question_count: Number(questionCount),
        p_duration_minutes: duration ? Number(duration) : null,
      });
      if (error) throw error;

      const r = (data ?? {}) as { added?: number; requested?: number };
      const added = Number(r.added ?? 0);
      const requested = Number(r.requested ?? 0);

      if (added === 0) {
        setMessage({
          type: "error",
          text: "Bu sınav türüne uygun doğrulanmış soru bulunamadı. Önce soru bankasına soru ekleyin veya AI ile soru üretin.",
        });
      } else if (added < requested) {
        setMessage({
          type: "success",
          text: `Deneme oluşturuldu. İstenen ${requested} sorudan ${added} tanesi eklendi (havuz yetersiz).`,
        });
        setResult({ added, requested });
      } else {
        setMessage({ type: "success", text: "Deneme başarıyla oluşturuldu." });
        setResult({ added, requested });
      }

      setTitle("");
      setDuration("");
      setQuestionCount("");
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Deneme oluşturulurken bir hata oluştu.",
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <SectionHeader
        title="AI Deneme Oluştur"
        description="Sınav türünü ve zorluğu seç; AI, doğrulanmış soru havuzundan denemeyi otomatik oluştursun."
      />

      <div className="rounded-lg border border-accent-300 bg-accent-100/60 p-4 flex items-start gap-3">
        <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-accent-500 text-background-50">
          <i className="ri-magic-line" aria-hidden="true"></i>
        </span>
        <p className="text-sm text-foreground-800 leading-relaxed">
          <span className="font-semibold">AI Sınav Motoru</span> sınav türüne ve zorluğa uygun, doğrulanmış soruları
          otomatik seçip denemeyi bir araya getirir. Soru bankasındaki içerik arttıkça denemeler de çeşitlenir.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="rounded-lg border border-background-200 bg-background-50 p-6">
        <div>
          <label className={labelClass}>Deneme Adı (boş bırakılırsa otomatik)</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputClass}
            placeholder={`Örn: ${examType || "TYT"} Deneme - 1`}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Sınav</label>
            <select value={examType} onChange={(e) => setExamType(e.target.value)} className={inputClass}>
              <option value="">Seçin...</option>
              {examTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Zorluk</label>
            <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className={inputClass}>
              {difficulties.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Soru Sayısı</label>
            <input
              type="number"
              min={1}
              max={200}
              value={questionCount}
              onChange={(e) => setQuestionCount(e.target.value)}
              className={inputClass}
              placeholder="Örn: 120"
            />
          </div>
          <div>
            <label className={labelClass}>Süre (dakika)</label>
            <input
              type="number"
              min={1}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className={inputClass}
              placeholder="Örn: 165"
            />
          </div>
        </div>

        {message && (
          <p
            className={`mt-4 text-sm rounded-md p-3 ${
              message.type === "success"
                ? "bg-primary-100 text-primary-800"
                : "bg-red-50 text-red-700"
            }`}
          >
            {message.text}
          </p>
        )}

        {result && result.added > 0 && (
          <div className="mt-4 flex items-center gap-3 rounded-md border border-background-200 bg-background-100/50 p-3">
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-check-double-line" aria-hidden="true"></i>
            </span>
            <p className="text-sm text-foreground-700">
              <span className="font-semibold">{result.added}</span> soru denemeye eklendi
              {result.added < result.requested && (
                <span className="text-foreground-500"> (istenen {result.requested})</span>
              )}
            </p>
          </div>
        )}

        <div className="mt-6 pt-5 border-t border-background-200">
          <button
            type="submit"
            disabled={generating}
            className="inline-flex items-center gap-2 whitespace-nowrap px-5 py-2.5 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {generating ? (
              <>
                <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                AI Deneme Oluşturuluyor...
              </>
            ) : (
              <>
                <i className="ri-magic-line" aria-hidden="true"></i>
                AI ile Deneme Oluştur
              </>
            )}
          </button>
        </div>
      </form>

      <PanelLinks
        links={[
          { label: "Soru Ekle", href: "/ogretmen/soru-ekle", icon: "ri-add-circle-line" },
          { label: "Test Oluştur", href: "/ogretmen/test-olustur", icon: "ri-file-list-3-line" },
        ]}
      />
    </div>
  );
}